<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFieldOrder } from 'dashboard/composables/useFieldOrder';
import { useProductsStore } from 'dashboard/stores/products';
import { STANDARD_FIELD_TYPES } from './productFormat';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DealCustomAttributeItem from 'dashboard/components-next/Deals/DealCustomAttributeItem.vue';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useProductsStore();
const { orderedFields } = useFieldOrder('product');

const dialogRef = ref(null);
const fileInputRef = ref(null);
const product = ref(null);
const isSaving = ref(false);
const nameError = ref(false);
const newGroupName = ref('');
const imageFile = ref(null);
const imagePreview = ref('');

const blankForm = () => ({
  name: '',
  sku: '',
  productGroupId: '',
  price: '',
  description: '',
  unit: '',
  specialPrice: '',
  wholesalePrice: '',
  loyaltyPoints: '',
  customAttributes: {},
});
const form = ref(blankForm());

const isCreate = computed(() => !product.value);
const title = computed(() =>
  isCreate.value
    ? t('PRODUCTS.FORM.CREATE_TITLE')
    : t('PRODUCTS.FORM.EDIT_TITLE')
);

const selectOption = computed(() => ({
  value: '',
  label: t('PRODUCTS.FORM.SELECT'),
}));
const unitOptions = computed(() => [
  selectOption.value,
  ...store.meta.units.map(unit => ({ value: unit, label: unit })),
]);
const groupOptions = computed(() => [
  selectOption.value,
  ...store.groups.map(group => ({ value: group.id, label: group.name })),
]);

const labelFor = field =>
  field.type === 'custom'
    ? field.attribute.attributeDisplayName
    : t(field.labelKey);

const typeOf = field =>
  field.type === 'custom' ? 'custom' : STANDARD_FIELD_TYPES[field.key];

// Chave do campo padrão → propriedade do formulário.
const FORM_KEYS = {
  sku: 'sku',
  price: 'price',
  description: 'description',
  special_price: 'specialPrice',
  wholesale_price: 'wholesalePrice',
  loyalty_points: 'loyaltyPoints',
};

const customAttributeWithValue = attribute => ({
  ...attribute,
  value: form.value.customAttributes?.[attribute.attributeKey] ?? '',
});

const handleCustomUpdate = (attributeKey, value) => {
  form.value.customAttributes = {
    ...form.value.customAttributes,
    [attributeKey]: value,
  };
};

const open = (existing = null) => {
  product.value = existing;
  nameError.value = false;
  newGroupName.value = '';
  imageFile.value = null;
  imagePreview.value = existing?.imageUrl || '';
  form.value = existing
    ? {
        name: existing.name,
        sku: existing.sku || '',
        productGroupId: existing.productGroupId || '',
        price: existing.price,
        description: existing.description || '',
        unit: existing.unit || '',
        specialPrice: existing.specialPrice,
        wholesalePrice: existing.wholesalePrice,
        loyaltyPoints: existing.loyaltyPoints ?? '',
        customAttributes: { ...existing.customAttributes },
      }
    : blankForm();
  dialogRef.value?.open();
};

const onImageSelected = event => {
  const [file] = event.target.files;
  if (!file) return;
  imageFile.value = file;
  imagePreview.value = URL.createObjectURL(file);
};

const addGroup = async () => {
  const name = newGroupName.value.trim();
  if (!name) return;
  try {
    const group = await store.createGroup(name);
    form.value.productGroupId = group.id;
    newGroupName.value = '';
  } catch {
    useAlert(t('PRODUCTS.MESSAGES.GROUP_ERROR'));
  }
};

const buildPayload = () => ({
  product: {
    name: form.value.name.trim(),
    sku: form.value.sku.trim(),
    product_group_id: form.value.productGroupId || null,
    price: form.value.price || 0,
    description: form.value.description,
    unit: form.value.unit || null,
    special_price: form.value.specialPrice || 0,
    wholesale_price: form.value.wholesalePrice || 0,
    loyalty_points:
      form.value.loyaltyPoints === '' ? null : Number(form.value.loyaltyPoints),
    custom_attributes: form.value.customAttributes,
  },
});

const save = async () => {
  nameError.value = !form.value.name.trim();
  if (nameError.value || isSaving.value) return;
  isSaving.value = true;
  try {
    let saved = await store.save(product.value?.id, buildPayload());
    if (imageFile.value)
      saved = await store.uploadImage(saved.id, imageFile.value);
    useAlert(
      t(
        isCreate.value
          ? 'PRODUCTS.MESSAGES.CREATE_SUCCESS'
          : 'PRODUCTS.MESSAGES.UPDATE_SUCCESS'
      )
    );
    emit('saved', saved);
    dialogRef.value?.close();
  } catch {
    useAlert(t('PRODUCTS.MESSAGES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="title"
    :confirm-button-label="t('PRODUCTS.FORM.SAVE')"
    :is-loading="isSaving"
    @confirm="save"
  >
    <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
      <div class="sm:col-span-2">
        <Input
          v-model="form.name"
          :label="t('PRODUCTS.FIELDS.NAME')"
          :message="nameError ? t('PRODUCTS.FORM.NAME_REQUIRED') : ''"
          :message-type="nameError ? 'error' : 'info'"
          custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
        />
      </div>
      <template v-for="field in orderedFields" :key="field.id">
        <div
          :class="{
            'sm:col-span-2': ['textarea', 'image'].includes(typeOf(field)),
          }"
        >
          <Input
            v-if="typeOf(field) === 'text'"
            v-model="form[FORM_KEYS[field.key]]"
            :label="labelFor(field)"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
          />
          <Input
            v-else-if="['money', 'integer'].includes(typeOf(field))"
            v-model="form[FORM_KEYS[field.key]]"
            type="number"
            min="0"
            :label="labelFor(field)"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
          />
          <TextArea
            v-else-if="typeOf(field) === 'textarea'"
            v-model="form.description"
            :label="labelFor(field)"
            :max-length="2000"
          />
          <div v-else-if="typeOf(field) === 'unit'" class="flex flex-col gap-1">
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ labelFor(field) }}
            </span>
            <ComboBox
              v-model="form.unit"
              :options="unitOptions"
              :placeholder="t('PRODUCTS.FORM.SELECT')"
            />
          </div>
          <div
            v-else-if="typeOf(field) === 'group'"
            class="flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ labelFor(field) }}
            </span>
            <ComboBox
              v-model="form.productGroupId"
              :options="groupOptions"
              :placeholder="t('PRODUCTS.FORM.SELECT')"
            />
            <div class="flex items-center gap-2 mt-1">
              <Input
                v-model="newGroupName"
                class="flex-1"
                :placeholder="t('PRODUCTS.FORM.NEW_GROUP_PLACEHOLDER')"
                custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
                @keydown.enter.prevent="addGroup"
              />
              <Button
                type="button"
                sm
                faded
                slate
                :label="t('PRODUCTS.FORM.ADD_GROUP')"
                @click="addGroup"
              />
            </div>
          </div>
          <div
            v-else-if="typeOf(field) === 'image'"
            class="flex flex-col gap-2"
          >
            <span class="text-heading-3 text-n-slate-12">
              {{ labelFor(field) }}
            </span>
            <div class="flex items-center gap-3">
              <img
                v-if="imagePreview"
                :src="imagePreview"
                class="object-cover rounded-lg size-16"
                alt=""
              />
              <button
                type="button"
                class="text-sm underline text-n-slate-12"
                @click="fileInputRef?.click()"
              >
                {{
                  imagePreview
                    ? t('PRODUCTS.FORM.CHANGE_IMAGE')
                    : t('PRODUCTS.FORM.UPLOAD')
                }}
              </button>
              <input
                ref="fileInputRef"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                class="hidden"
                @change="onImageSelected"
              />
            </div>
          </div>
          <DealCustomAttributeItem
            v-else-if="typeOf(field) === 'custom'"
            :attribute="customAttributeWithValue(field.attribute)"
            @update="handleCustomUpdate"
          />
        </div>
      </template>
    </div>
  </Dialog>
</template>
